/**
 * Push Notification Service
 * Handles FCM token management and push notification setup
 */

import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from './firebase';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

// VAPID key - you'll need to generate this in Firebase Console
const VAPID_KEY = 'BDJIPURUEjW1YjlaBSFkw5xB-5No3_ygEJLaBHiBkL2MUqwnR5vNB-mun2ThWNDLyP6papX5HshsfXd07Ody8T0';

/**
 * Check if push notifications are supported
 */
export const isPushNotificationSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && messaging !== null;
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  try {
    if (!isPushNotificationSupported()) {
      throw new Error('Push notifications are not supported');
    }

    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      return true;
    } else {
      console.log('Notification permission denied');
      return false;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Get FCM token
 */
export const getFCMToken = async () => {
  try {
    if (!messaging) {
      throw new Error('Messaging not initialized');
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY
    });

    if (token) {
      console.log('FCM Token:', token);
      return token;
    } else {
      console.log('No registration token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Save FCM token to user's profile
 */
export const saveFCMTokenToProfile = async (userId, token) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Get current user data
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const existingTokens = userData.fcmTokens || [];
      
      // Add new token if it doesn't exist
      if (!existingTokens.includes(token)) {
        await updateDoc(userRef, {
          fcmTokens: [...existingTokens, token],
          lastTokenUpdate: new Date()
        });
      }
    } else {
      // Create user document with token
      await setDoc(userRef, {
        fcmTokens: [token],
        lastTokenUpdate: new Date()
      }, { merge: true });
    }

    console.log('FCM token saved to profile');
  } catch (error) {
    console.error('Error saving FCM token:', error);
  }
};

/**
 * Remove FCM token from user's profile (on logout)
 */
export const removeFCMTokenFromProfile = async (userId, token) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const existingTokens = userData.fcmTokens || [];
      
      // Remove the token
      const updatedTokens = existingTokens.filter(t => t !== token);
      
      await updateDoc(userRef, {
        fcmTokens: updatedTokens,
        lastTokenUpdate: new Date()
      });
    }

    console.log('FCM token removed from profile');
  } catch (error) {
    console.error('Error removing FCM token:', error);
  }
};

/**
 * Initialize push notifications for a user
 */
export const initializePushNotifications = async (userId) => {
  try {
    if (!isPushNotificationSupported()) {
      console.log('Push notifications not supported');
      return false;
    }

    // Request permission
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      return false;
    }

    // Get FCM token
    const token = await getFCMToken();
    if (!token) {
      return false;
    }

    // Save token to user profile
    await saveFCMTokenToProfile(userId, token);

    // Set up foreground message listener
    setupForegroundMessageListener();

    return true;
  } catch (error) {
    console.error('Error initializing push notifications:', error);
    return false;
  }
};

/**
 * Setup foreground message listener
 */
const setupForegroundMessageListener = () => {
  if (!messaging) return;

  onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);
    
    // Show notification when app is in foreground
    showForegroundNotification(payload);
  });
};

/**
 * Show notification when app is in foreground
 */
const showForegroundNotification = (payload) => {
  const { notification, data } = payload;
  
  // Create notification options
  const options = {
    body: notification?.body || 'You have a new notification',
    icon: '/logo192.png',
    badge: '/favicon.ico',
    tag: data?.type || 'general',
    data: data || {},
    requireInteraction: true
  };

  // Check if we have permission to show notifications
  if (Notification.permission === 'granted') {
    const notificationInstance = new Notification(
      notification?.title || 'VarsityLink',
      options
    );

    // Handle notification click
    notificationInstance.onclick = () => {
      window.focus();
      
      // Navigate based on notification type
      const url = data?.url || '/';
      if (url !== '/') {
        window.location.href = url;
      }
      
      notificationInstance.close();
    };

    // Auto close after 5 seconds
    setTimeout(() => {
      notificationInstance.close();
    }, 5000);
  }
};

/**
 * Get notification permission status
 */
export const getNotificationPermissionStatus = () => {
  if (!('Notification' in window)) {
    return 'not-supported';
  }
  return Notification.permission;
};

/**
 * Cleanup push notifications (call on logout)
 */
export const cleanupPushNotifications = async (userId) => {
  try {
    // Get current token
    const token = await getFCMToken();
    
    if (token && userId) {
      // Remove token from user profile
      await removeFCMTokenFromProfile(userId, token);
    }
  } catch (error) {
    console.error('Error cleaning up push notifications:', error);
  }
};

/**
 * Test push notification (for development)
 */
export const testPushNotification = () => {
  if (Notification.permission === 'granted') {
    new Notification('Test Notification', {
      body: 'This is a test notification from VarsityLink',
      icon: '/logo192.png',
      badge: '/favicon.ico'
    });
  }
};

export default {
  isPushNotificationSupported,
  requestNotificationPermission,
  getFCMToken,
  initializePushNotifications,
  cleanupPushNotifications,
  getNotificationPermissionStatus,
  testPushNotification
};